import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero } from "@/components/common/Bits";
import { Reveal } from "@/components/motion/Reveal";
import { SkeletonCard, EmptyState } from "@/components/common/States";
import { useCategories } from "@/hooks/useContent";
import { getBlogPosts } from "@/services/api";

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cat, setCat] = useState("All");
    const [query, setQuery] = useState("");
    const categories = useCategories(posts);

    useEffect(() => {
        let active = true;
        getBlogPosts().then((data) => {
            if (!active) return;
            setPosts(data);
            setLoading(false);
        });
        return () => {
            active = false;
        };
    }, []);

    const filtered = useMemo(() => {
        return posts.filter((p) => {
            const matchesCat = cat === "All" || p.category === cat;
            const q = query.trim().toLowerCase();
            const matchesQuery =
                !q ||
                [p.title, p.excerpt, p.category]
                    .filter(Boolean)
                    .some((field) => String(field).toLowerCase().includes(q));
            return matchesCat && matchesQuery;
        });
    }, [posts, cat, query]);

    const fmt = (d) => new Date(d).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" });

    return (
        <>
            <SEO
                title="Blog — Property Care Tips for Every Move"
                path="/blog"
                description="Practical guides on moving, cleaning, painting, maintenance and property handover in Singapore from the ProCare.SG team."
            />
            <PageHero
                label="Blog"
                title="Property CARE Tips for Every MOVE"
                subtitle="Practical guides on moving, cleaning, painting, maintenance and handover in Singapore."
                image="/images/painting.webp"
            />

            <section className="py-16">
                <div className="mx-auto max-w-7xl px-6">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex flex-wrap gap-2">
                            {categories.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setCat(c)}
                                    data-testid={`blog-filter-${c.toLowerCase()}`}
                                    className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${cat === c ? "bg-brand-red text-white" : "bg-brand-surface text-brand-blue hover:bg-brand-blue/10"}`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                        <div className="relative w-full lg:w-72">
                            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-ink/60" />
                            <input
                                type="text"
                                placeholder="Search articles…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                data-testid="blog-search"
                                className="w-full rounded-full border border-brand-blue/15 bg-white py-2.5 pl-11 pr-4 text-sm outline-none focus:border-brand-blue"
                            />
                        </div>
                    </div>

                    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {loading ? (
                            [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
                        ) : filtered.length ? (
                            filtered.map((p, i) => (
                                <Reveal key={p.id} delay={(i % 3) * 0.06}>
                                    <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-brand-blue/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-brand">
                                        <div className="aspect-[16/10] overflow-hidden">
                                            <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                        </div>
                                        <div className="flex flex-1 flex-col p-6">
                                            <span className="text-xs font-bold uppercase tracking-widest text-brand-red">{p.category}</span>
                                            <h2 className="mt-2 font-display text-lg font-semibold leading-snug text-brand-blue">{p.title}</h2>
                                            <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-ink/60">{p.excerpt}</p>
                                            <span className="mt-4 text-xs text-brand-ink/60">{fmt(p.date)}</span>
                                        </div>
                                    </Link>
                                </Reveal>
                            ))
                        ) : (
                            <div className="md:col-span-2 lg:col-span-3">
                                <EmptyState title="No articles found" desc="Try a different category or search." />
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
